import type { GpAssignmentMapPoint } from '../../data-access/types/gp-sampling-assignment';

import { samplerColorForId } from './point-colors';

export function LegendSwatch({
  fill,
  stroke = '#ffffff',
  strokeWidth = 1,
  opacity = 1,
  size = 12,
}: {
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  size?: number;
}) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: fill,
        opacity,
        border: `${strokeWidth}px solid ${stroke}`,
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
  );
}

export function buildSamplerLegendItems(
  points: GpAssignmentMapPoint[],
  legendMode: 'organization' | 'user' = 'user',
) {
  const entries = new Map<number, string>();
  const orgColors = new Map<number, { label: string; color: string }>();
  let hasUnassigned = false;

  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    if (point.sampler) {
      const organization = point.sampler_organization;
      if (organization?.color) {
        if (!orgColors.has(organization.id)) {
          orgColors.set(organization.id, {
            label: organization.name ?? `#${organization.id}`,
            color: organization.color,
          });
        }
      } else if (legendMode === 'organization') {
        if (organization?.id != null && !entries.has(organization.id)) {
          entries.set(organization.id, organization.name ?? `#${organization.id}`);
        }
      } else if (!entries.has(point.sampler.id)) {
        entries.set(
          point.sampler.id,
          `${point.sampler.first_name} ${point.sampler.last_name}`,
        );
      }
    } else {
      hasUnassigned = true;
    }
  }

  if (orgColors.size > 0) {
    const items = Array.from(orgColors.entries())
      .map(([id, item]) => ({
        id,
        label: item.label,
        color: item.color,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { items, hasUnassigned };
  }

  const items = Array.from(entries.entries())
    .map(([id, label]) => ({
      id,
      label,
      color: samplerColorForId(id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { items, hasUnassigned };
}

export function buildLaboratoryLegendItems(points: GpAssignmentMapPoint[]) {
  const entries = new Map<number, string>();
  let hasUnassigned = false;

  for (let i = 0; i < points.length; i += 1) {
    const laboratory = points[i].laboratory;
    if (laboratory) {
      if (!entries.has(laboratory.id)) {
        entries.set(laboratory.id, laboratory.name);
      }
    } else {
      hasUnassigned = true;
    }
  }

  const items = Array.from(entries.entries())
    .map(([id, label]) => ({
      id,
      label,
      color: samplerColorForId(id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { items, hasUnassigned };
}
